import UIActions from "../actions/UIActions";
import AppConstants from "../constants/AppConstants";

const {
  TOAST_SUCCESS,
  TOAST_WARNING,
  TOAST_ERROR,
  MAX_PAGES,
  MAX_PAGE_WARNING_FREQUENCY,
  MAX_PAGE_WARNING_COUNTER,
  MAX_GALLERY_WARNING_COUNTER,
  MAX_GALLERY_IMAGES,
  MAX_GALLERY_WARNING_FREQUENCY,
} = AppConstants;

/**
 * @param {String} val - Value to be copied to clipboard
 * @param {Boolean} showToast - Whether to show a toast on success or failure
 * @param {String} successText - Text to show on success
 * @param {String} errorText - Text to show on error
 * @param {Number} dur - Duration of toast
 */
export const copyToClipboard = ({
  val,
  showToast,
  successText = "Copied!",
  errorText = "Failed to copy :(",
  dur,
}) =>
  navigator.clipboard
    .writeText(val)
    .then(() => {
      showToast &&
        UIActions.showToast({
          title: "Success",
          message: successText,
          status: TOAST_SUCCESS,
          dur,
        });
    })
    .catch(() => {
      showToast &&
        UIActions.showToast({
          title: "ERROR",
          message: errorText,
          status: TOAST_ERROR,
        });
    });

/**
 * @param {String} numberOfPages - Number Of Pages in Site
 */
export const checkAndHandleMaxPageQuantity = (numberOfPages) => {
  checkMaxAndHandleWarning(
    numberOfPages,
    MAX_PAGES,
    MAX_PAGE_WARNING_COUNTER,
    MAX_PAGE_WARNING_FREQUENCY,
    `Performance will start to decrease the more pages you add. We recommend staying below 100 pages.`
  );
};

/**
 * @param {String} numberOfImages - Number Of Images in Gallery
 */
export const checkAndHandleMaxGalleryWarning = (numberOfImages) => {
  checkMaxAndHandleWarning(
    numberOfImages,
    MAX_GALLERY_IMAGES,
    MAX_GALLERY_WARNING_COUNTER,
    MAX_GALLERY_WARNING_FREQUENCY,
    `Performance will start to decrease the more images you add to each gallery. We recommend staying below ${MAX_GALLERY_IMAGES} images per gallery.`
  );
};

/**
 *
 * @param numberOfItems - Number of items
 * @param maxItems - Max number of items
 * @param warningCounterKey - Key to store the warning counter in local storage
 * @param maxWarningFrequency - Max number of times the warning should be shown
 * @param warningMessage - Warning message to show
 */
export const checkMaxAndHandleWarning = (
  numberOfItems,
  maxItems,
  warningCounterKey,
  maxWarningFrequency,
  warningMessage
) => {
  // If the warning counter doesn't exist, set it to 0
  if (!localStorage.getItem(warningCounterKey)) {
    localStorage.setItem(warningCounterKey, "0");
  }

  // Get the warning counter
  const counter = parseInt(
    localStorage.getItem(warningCounterKey),
    AppConstants.PARSE_INT_DEFAULT_RADIX
  );

  // If the number of items is greater than the max, increment the counter
  if (numberOfItems > maxItems) {
    localStorage.setItem(warningCounterKey, (counter + 1).toString());
  }

  // If the number of items is equal to the max or the counter is a multiple of the max warning frequency, show the warning
  if (
    numberOfItems === maxItems ||
    (counter % maxWarningFrequency === 0 && numberOfItems > maxItems)
  ) {
    localStorage.setItem(warningCounterKey, "1");
    UIActions.showToast({
      title: "HEADS UP!",
      message: warningMessage,
      status: TOAST_WARNING,
      dur: 30000, // 30 seconds
    });
  }
};

/**
 * @param {String} pageName - Page Name
 * @param {String} pageSize - Page Size
 */
export const checkAndHandleMaxPageSize = (pageName, pageSize) => {
  if (pageSize > AppConstants.MAX_PAGE_SIZE_BYTES) {
    UIActions.showToast({
      title: "HEADS UP!",
      message: `${pageName} page is too big. To continue working on this page, consider splitting the content into multiple pages. Also, remove unneeded elements, canvases, and canvas views.`,
      status: TOAST_WARNING,
      dur: 30000, // 30 seconds
    });
  }
};
